import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Film, Users, Eye, Search } from 'lucide-react';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { fetchWarmUps } from '../../services/warmUpService';

const DIFFICULTY_STYLES = {
  Beginner: 'bg-green-100 text-green-700',
  Intermediate: 'bg-yellow-100 text-yellow-700',
  Advanced: 'bg-red-100 text-red-700',
};

const WarmUpsBrowse = () => {
  const navigate = useNavigate();
  const [warmUps, setWarmUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchWarmUps({ adminMode: false })?.then(setWarmUps)?.catch((err) => setError(err?.message || 'Failed to load warm ups.'))?.finally(() => setLoading(false));
  }, []);

  const getTalentName = (wu) => {
    const t = wu?.talent;
    if (!t) return null;
    return t?.display_name || `${t?.first_name || ''} ${t?.last_name || ''}`?.trim() || null;
  };

  const filtered = warmUps?.filter((wu) =>
    !searchQuery ||
    wu?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    (getTalentName(wu) || '')?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center h-64"><LoadingSpinner /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Warm Ups — RoleCall</title></Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Warm Ups</h1>
          <p className="text-muted-foreground mt-2">Daily vocal warm up videos to help you sing better every day</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700 text-sm">{error}</div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder="Search warm ups..."
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-xl text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Grid */}
        {filtered?.length === 0 ? (
          <div className="text-center py-16">
            <Film className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-foreground font-medium">{searchQuery ? 'No warm ups match your search' : 'No warm ups available yet'}</p>
            <p className="text-muted-foreground text-sm mt-1">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered?.map((wu) => {
              const talentName = getTalentName(wu);
              const thumbnailUrl = wu?.mux_playback_id
                ? `https://image.mux.com/${wu?.mux_playback_id}/thumbnail.jpg?width=640&height=360&fit_mode=smartcrop`
                : null;

              return (
                <button
                  key={wu?.id}
                  onClick={() => navigate(`/warm-ups/${wu?.id}`)}
                  className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all duration-200 text-left group"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-muted overflow-hidden">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={`Thumbnail for ${wu?.title}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-10 h-10 text-muted-foreground opacity-40" />
                      </div>
                    )}
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[14px] border-l-gray-800 ml-1" />
                      </div>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground text-sm line-clamp-2 leading-snug">{wu?.title}</h3>
                    {wu?.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{wu?.description}</p>
                    )}
                    {wu?.difficulty_level && (
                      <span className={`inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${DIFFICULTY_STYLES?.[wu?.difficulty_level] || 'bg-gray-100 text-gray-600'}`}>
                        {wu?.difficulty_level}
                      </span>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      {talentName ? (
                        <div className="flex items-center gap-1.5">
                          {wu?.talent?.headshot_url ? (
                            <img src={wu?.talent?.headshot_url} alt={talentName} className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <Users className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="text-xs text-muted-foreground">{talentName}</span>
                        </div>
                      ) : <span />}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Eye className="w-3.5 h-3.5" />
                        <span>{wu?.view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WarmUpsBrowse;
